'use client'

import type { ProductCardProps } from '@maestro/commerce-blocks/components/product-card/product-card.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'

import { useProductCardQuantityPicker } from '@/components/product-card/batons/use-product-card-quantity-picker'
import { useDomainType } from '@/hooks/use-domain-type'
import { isPurchasable } from '@/utils/stock.utils'
import { ColorPreviews } from '@maestro/commerce-blocks/components/color-previews'
import { ImageProduct } from '@maestro/commerce-blocks/components/image-product'
import { ProductBrand } from '@maestro/commerce-blocks/components/product-brand'
import {
  ProductCardAddToCartButtonWrapper,
  ProductCardReviewSummary,
  ProductCardWishlistWrapper,
} from '@maestro/commerce-blocks/components/product-card'
import { useProductCard } from '@maestro/commerce-blocks/components/product-card/batons/use-product-card'
import { ProductIdentifier } from '@maestro/commerce-blocks/components/product-identifier'
import { ProductLabels } from '@maestro/commerce-blocks/components/product-labels'
import { ProductPackagingUnit } from '@maestro/commerce-blocks/components/product-packaging-unit'
import { ProductPrice } from '@maestro/commerce-blocks/components/product-price'
import { ProductQuantityPicker } from '@maestro/commerce-blocks/components/product-quantity-picker'
import { useProductListingDataContext } from '@maestro/commerce-blocks/providers/product-listing-data'
import { useProductListingReviewsContext } from '@maestro/commerce-blocks/providers/product-listing-reviews'
import { theme } from '@maestro/local/theme'
import { Link } from '@pearlconvert/ui/components/link'
import { StockStatus } from '@pearlconvert/ui/components/stock-status'

import '@maestro/commerce-blocks/components/product-card/styles/product-card.scss'

export default function ProductCard(props: ProductCardProps) {
  const {
    product: {
      displayStockStatus: { productCardDefault: defaultDisplayStockStatus },
      displayPrice: { productCardDefault: defaultDisplayPrice },
      isVisibleBrand,
      isVisibleSku,
    },
  } = theme()

  const {
    product,
    showWishlistButton = true,
    addToCartType = 'none',
    addToCartTitle,
    addToCartNotificationType,
    displayStockStatus = defaultDisplayStockStatus,
    displayPrice = defaultDisplayPrice,
    isStockLoading,
    enableLazyLoading,
    imageConfig = 'plp',
    isCompact = false,
    loading,
    fetchPriority = 'auto',
    recommendationProps,
    customButton,
    eventProps,
    titleTag: TitleTag = 'h2',
  } = props
  const {
    productImage,
    stockStatus,
    setProductData,
    uniqueSizes,
    wishlistButtonProps,
    handleWishlistToggle,
    displayCardActions,
  } = useProductCard({
    product,
    displayStockStatus,
    recommendationProps,
    eventProps,
  })

  const { isLoadingReviewSummary, getProductReviewSummary, showReviewSummary } =
    useProductListingReviewsContext()

  const { getProductPrice, getProductStock, isLoadingDataSummary } =
    useProductListingDataContext()
  const productPrice = getProductPrice(product.id)
  const productStock = getProductStock(product.id)

  const t = useTranslations('commerce-blocks')
  // Polar: stock status is shown to retail only (POL-155). The stock itself is
  // still used for the purchasable checks below.
  const domainType = useDomainType()
  const cardStock = productStock ?? stockStatus ?? null

  const { quantity, setQuantity, maxQuantity, displayQuantityPicker } =
    useProductCardQuantityPicker({ product, addToCartType })

  return (
    <div
      className={classNames('product-card', {
        'product-card--compact': isCompact,
      })}
    >
      <Link
        href={`/${product.urlPath}`}
        className="product-card__image-wrapper"
        title={product.name}
        onClick={setProductData}
      >
        <ImageProduct
          src={productImage.url}
          imageConfig={imageConfig}
          base64Blur={productImage.blurDataUrl}
          alt={productImage.label ?? product.name ?? ''}
          enableLazyLoading={enableLazyLoading}
          loading={loading}
          fetchPriority={fetchPriority}
        />
      </Link>
      <div className="product-card__description">
        <ProductLabels
          labels={product.labels}
          className="product-card__labels"
        />
        <div className="product-card__info">
          {isVisibleSku && (
            <ProductIdentifier
              product={product}
              className="product-card__sku"
            />
          )}
          {isVisibleBrand && (
            <ProductBrand
              className="product-card__brand"
              brand={product.brand}
            />
          )}
          <TitleTag className="product-card__title">
            <Link
              href={`/${product.urlPath}`}
              className="product-card__title-link"
              onClick={setProductData}
              tabIndex={-1}
            >
              {product.name}
            </Link>
          </TitleTag>
          {isCompact && (
            <ProductPackagingUnit
              className="product-card__packaging-unit--compact"
              packagingUnit={product.packagingUnit}
            />
          )}
          {!!product.attributes?.length && (
            <div className="product-card__variant">
              {product.attributes
                .map(({ value }) => value)
                .filter(Boolean)
                .join(', ')}
            </div>
          )}
        </div>
        <ColorPreviews product={product} />
        {uniqueSizes.length > 1 && (
          <ul className="product-configuration__size">
            {uniqueSizes.slice(0, 4).map((size) => (
              <li className="product-configuration__size-item" key={size}>
                {size}
              </li>
            ))}
          </ul>
        )}
        {showReviewSummary && !isCompact && (
          <ProductCardReviewSummary
            isLoading={isLoadingReviewSummary}
            reviewSummary={getProductReviewSummary(product.id)}
          />
        )}
        {displayStockStatus && domainType === 'retail' && (
          <StockStatus
            position="left"
            stock={cardStock}
            // Backend availability text replaces the label when present.
            customLabel={cardStock?.availabilityText}
            isLoading={isStockLoading || isLoadingDataSummary}
            infoType="compact"
          />
        )}
        <div className="product-card__price">
          {displayPrice &&
            (isPurchasable(cardStock) ? (
              <ProductPrice
                price={productPrice ?? product.price}
                isLoading={isLoadingDataSummary}
              />
            ) : (
              // Not purchasable: the contact-sales quote takes the price slot.
              <span className="product-card__quote">
                {t('price.contact-sales-quote')}
              </span>
            ))}
          {!isCompact && (
            <ProductPackagingUnit
              className="product-card__packaging-unit"
              packagingUnit={product.packagingUnit}
            />
          )}
        </div>
        {displayCardActions && (
          <div className="product-card__action">
            {displayQuantityPicker && (
              <ProductQuantityPicker
                id={product.id}
                quantity={quantity}
                maxQuantity={maxQuantity}
                onQuantityChange={setQuantity}
                disabled={!isPurchasable(cardStock)}
                controlSize="normal"
              />
            )}
            {customButton}
            {showWishlistButton && (
              <ProductCardWishlistWrapper
                product={product}
                handleWishlistToggle={handleWishlistToggle}
                wishlistButtonProps={wishlistButtonProps}
              />
            )}
            {addToCartType === 'icon' && (
              <ProductCardAddToCartButtonWrapper
                product={product}
                variantId={product.id}
                quantity={quantity}
                className="product-card__button--addtocart"
                hideTitle
                notificationType={addToCartNotificationType}
                eventProps={eventProps}
                isDisabled={!isPurchasable(cardStock)}
              />
            )}
            {addToCartType === 'button' && (
              <ProductCardAddToCartButtonWrapper
                product={product}
                variantId={product.id}
                quantity={quantity}
                className="product-card__button--addtocart"
                title={addToCartTitle}
                notificationType={addToCartNotificationType}
                eventProps={eventProps}
                isDisabled={!isPurchasable(cardStock)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
